import React, { useEffect } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { ValidationBadge, ValidationSummary } from '../../components/progression/ValidationBadge';
import { NpcCard } from '../../components/progression/NpcCard';
import { ChecklistBar } from '../../components/progression/ChecklistBar';
import { MaterialList } from '../../components/progression/MaterialList';
import { ItemLevelExplainer } from '../../components/progression/ItemLevelExplainer';
import { GuidePanel, FactList, Note } from '../../components/progression/GuidePanel';
import { RecipeSteps, ClassicRecipeLabel } from '../../components/progression/RecipeSteps';
import { getRecipe, recipePath, recipeIngredients } from '../../data/guide/recipes';
import { useChecklist } from '../../hooks/useChecklist';
import { useWorldLookup } from '../../hooks/useWorldLookup';
import { useHashScroll } from '../../hooks/useHashScroll';
import { useNpcLocation } from '../../hooks/useNpcLocation';
import { npcLocationValidation } from '../../utils/npcLocation';
import { itemPath } from '../../utils/worldFormat';
import { summarizeMaterials, type WorldLookup } from '../../utils/progressionLookup';
import type { Recipe, RecipeResult } from '../../types/progression';
export function RecipePage() {
  const {
    recipeId
  } = useParams();
  const r = getRecipe(recipeId);
  if (!r) return <Navigate to="/guia/combinaciones" replace data-id="element-2387" data-mp="ojkl4cu9d54t" />;
  return <RecipeView key={r.id} r={r} data-id="element-2388" data-mp="tjqfi0rwx5ql" />;
}
function RecipeView({
  r
}: {
  r: Recipe;
}) {
  const [params] = useSearchParams();
  const from = getRecipe(params.get('desde') ?? undefined);
  const list = useChecklist(`receta-${r.id}`);
  const {
    status,
    lookup
  } = useWorldLookup();
  useHashScroll();
  const allIngredients = recipeIngredients(r).filter(m => m.gameName || m.itemId);
  const sum = summarizeMaterials(lookup, allIngredients);
  const isWings = !r.group || r.group === 'alas';
  const parent = isWings ? {
    label: 'Alas y capas',
    to: '/guia/alas'
  } : {
    label: 'Combinaciones',
    to: '/guia/combinaciones'
  };
  const machine = r.machine ?? 'Chaos Machine';
  const npcLoc = useNpcLocation(r.npc);
  const npcRow = npcLoc ? npcLocationValidation(npcLoc) : {
    status: 'pendiente' as const,
    detail: ''
  };
  useEffect(() => {
    document.title = `${r.shortTitle} · ${parent.label} · Guía MU Vanguard`;
  }, [r, parent.label]);
  const groups = r.variants ? r.variants.map(v => ({
    prefix: v.id,
    ingredients: v.ingredients
  })) : [{
    prefix: 'ing',
    ingredients: r.ingredients
  }];
  const keys = groups.flatMap(g => g.ingredients.map(m => `${g.prefix}:${m.id}`));
  const done = keys.filter(k => list.isChecked(k)).length;
  const needsLevel = recipeIngredients(r).some(m => m.requirement?.includes('opción adicional'));
  const prev = getRecipe(r.prev);
  const next = getRecipe(r.next);
  return <div className="space-y-8 md:space-y-10" data-id="element-2389" data-mp="12t4swucwpm7u">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, parent, {
      label: r.shortTitle
    }]} eyebrow={`Receta · ${machine}`} title={r.title} data-id="element-2390" data-mp="12s37px26fjce">
        <p className="mt-2 max-w-2xl text-sm text-gray-300 md:text-base" data-id="element-2391" data-mp="tt81wm31ob9m">{r.summary}</p>
        <div className="mt-3" data-id="element-2392" data-mp="xu1kr11mqzvn">
          <ClassicRecipeLabel data-id="element-2393" data-mp="xv2yjda160h2" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-2394" data-mp="xw4kicsl47dk">
          {from && <Link to={recipePath(from.id)} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#050b14] hover:bg-[#F3E5AB]" data-id="element-2395" data-mp="1lkntyvndeq78">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-2396" data-mp="1cqsqds1wd7tp" />
              Volver a {from.shortTitle}
            </Link>}
          <CopyLinkButton data-id="element-2397" data-mp="1gqt2h61ix6xl" />
        </div>
      </WorldPageHeader>

      <FactList facts={[{
      label: 'Dónde se combina',
      value: machine,
      status: 'clasica'
    }, {
      label: 'Zen',
      value: r.zen,
      status: r.zenStatus
    }, {
      label: 'Probabilidad de éxito',
      value: 'Se publicará solo con la fórmula confirmada',
      status: 'pendiente'
    }, {
      label: 'Si la combinación falla',
      value: r.failure,
      status: r.failureStatus
    }]} data-id="element-2398" data-mp="24hyj3nfag38y" />
      <NpcCard npcId={r.npc} lookup={lookup} data-id="element-2399" data-mp="bnhbtc6xknt6" />

      <ChecklistBar list={list} total={keys.length} done={done} data-id="element-2400" data-mp="7lndvbo4dxwp" />

      {r.variants ? r.variants.map(v => <GuidePanel key={v.id} id={`capa-${v.id}`} eyebrow={v.who} title={v.title} data-id="element-2401" data-mp="14bwdkx11x9mk">
            <Note tone={v.id === 'rf' ? 'warn' : 'info'} data-id="element-2402" data-mp="zc1ngvp3oo2k">{v.note}</Note>
            <MaterialList materials={v.ingredients} list={list} prefix={v.id} lookup={lookup} status={status} fromRecipe={r.id} data-id="element-2403" data-mp="zew8aoc10awi" />
            <Results results={v.results} lookup={lookup} data-id="element-2404" data-mp="qkrhu60t11hi" />
          </GuidePanel>) : <>
          <GuidePanel id="ingredientes" title="Ingredientes" data-id="element-2405" data-mp="1n35y01cd1isx">
            <MaterialList materials={r.ingredients} list={list} prefix="ing" lookup={lookup} status={status} fromRecipe={r.id} data-id="element-2406" data-mp="1ibfhfdmtxyt2" />
          </GuidePanel>
          <GuidePanel id="resultado" title="Resultado posible" data-id="element-2407" data-mp="30zqi2xj60gcm">
            {r.resultNote && <Note data-id="element-2408" data-mp="35ya5z3hbk27m">{r.resultNote}</Note>}
            <Results results={r.results} lookup={lookup} data-id="element-2409" data-mp="35zcwvben64nj" />
          </GuidePanel>
        </>}

      {r.steps && r.steps.length > 0 && <GuidePanel id="pasos" title="Cómo hacerlo" data-id="element-2410" data-mp="3eygiam1a6w57">
          <RecipeSteps steps={r.steps} data-id="element-2411" data-mp="3evu78cf6lyi8" />
        </GuidePanel>}

      {needsLevel && <ItemLevelExplainer data-id="element-2412" data-mp="3jwi6tmhh7no2" />}

      <ValidationSummary rows={[{
      label: 'Identidad de los objetos',
      status: sum?.identity.status ?? 'pendiente',
      detail: sum?.identity.detail ?? 'Cargando datos…'
    }, {
      label: 'Dónde se obtienen los ingredientes',
      status: sum?.routes.status ?? 'pendiente',
      detail: sum?.routes.detail ?? 'Cargando datos…'
    }, {
      label: 'Ingredientes y cantidades en Vanguard',
      status: 'clasica',
      detail: r.source ? `Referencia: ${r.source}` : 'Referencia clásica de Season 6'
    }, {
      label: 'Zen',
      status: r.zenStatus === 'clasica' ? 'clasica' : 'pendiente',
      detail: r.zen
    }, {
      label: 'Probabilidad de éxito y posible cambio por VIP',
      status: 'pendiente'
    }, {
      label: 'Efectos del fallo',
      status: 'pendiente'
    }, {
      label: 'Ubicación del NPC',
      ...npcRow
    }, {
      label: 'NPC que realiza la combinación en Vanguard',
      status: 'pendiente',
      detail: r.npc === 'pet-trainer' ? 'Trainer (226), confirmado por el staff; falta validar su ubicación en Vanguard' : 'Pendiente de confirmar por administración'
    }, {
      label: 'Prueba dentro del juego',
      status: 'sin-probar'
    }]} data-id="element-2413" data-mp="3jv731mo16fwy" />

      <nav aria-label="Recetas relacionadas" className="grid gap-2 sm:grid-cols-2" data-id="element-2414" data-mp="307pdnps02gcr">
        {prev && <Link to={recipePath(prev.id)} className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-4 hover:border-[#D4AF37]/40" data-id="element-2415" data-mp="2vb8msntf1nvz">
            <ArrowLeft className="h-4 w-4 text-gray-500 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-2416" data-mp="10wfuoyrqrf8s" />
            <span data-id="element-2417" data-mp="3ciq6l9l8vkig"><span className="block text-xs text-gray-400" data-id="element-2418" data-mp="3cizwkx6ahro5">Paso anterior</span><span className="font-cinzel font-bold text-white" data-id="element-2419" data-mp="3hict69myqd3x">{prev.shortTitle}</span></span>
          </Link>}
        {next && <Link to={recipePath(next.id)} className="group flex items-center justify-end gap-3 rounded-2xl border border-[#D4AF37]/30 bg-[#0A1628] p-4 text-right hover:border-[#D4AF37]/60 sm:col-start-2" data-id="element-2420" data-mp="32sezxfa6ex7v">
            <span data-id="element-2421" data-mp="2trqisbphkgqr"><span className="block text-xs text-gray-400" data-id="element-2422" data-mp="2tryyscgwo266">Siguiente</span><span className="font-cinzel font-bold text-white" data-id="element-2423" data-mp="2yrldkqcdtqh3">{next.shortTitle}</span></span>
            <ArrowRight className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2424" data-mp="2juu9i3rrjql5" />
          </Link>}
      </nav>
    </div>;
}
function Results({
  results,
  lookup
}: {
  results: RecipeResult[];
  lookup?: WorldLookup;
}) {
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-2425" data-mp="i4air0r33ah9">
      {results.map(x => {
      const ref = lookup?.identify(x);
      return <li key={x.gameName} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-[#0A1628] p-3" data-id="element-2426" data-mp="n07wczayq2qc">
            <span className="min-w-0" data-id="element-2427" data-mp="31yqcslwbzvoj">
              {ref ? <Link to={itemPath(ref.id)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2428" data-mp="321ctpc543njf">{ref.name}</Link> : <span className="font-semibold text-white" data-id="element-2429" data-mp="2s7ciqtn5fo2s">{x.gameName}</span>}
              {x.who && <span className="block text-xs text-gray-400" data-id="element-2430" data-mp="2n89dp9765hut">{x.who}</span>}
            </span>
            {lookup && (ref ? <ValidationBadge status="identificado" data-id="element-2431" data-mp="2jb4wfao3frb5" /> : <ValidationBadge status="pendiente" label="No identificado en el catálogo" data-id="element-2432" data-mp="2eaqn7parabbj" />)}
          </li>;
    })}
    </ul>;
}